import { QueryOptions } from "@apollo/client";
import apollo from "../../clients/apollo";
import { achievementsGQL } from "../queries/achievements";
import {
  FullTransaction,
  GetLeaderboardsInput,
  HouseBattle,
  ListTournamentInput,
  Query,
  SearchUsersInput,
  Status,
} from "../graphql";

import { ApiLegacyName } from "../../constants/apollo";
import {
  getHouseMatchGQL,
  getMatchGQL,
  userHouseBattlesGQL,
} from "../queries/battles";
import { getChallengeGQL, getContinuePlayingGQL } from "../queries/challenge";
import { getContestsGQL } from "../queries/contests";
import { eventRulesGQL } from "../queries/eventRules";
import {
  getGameGQL,
  getHouseBattleByGameIdGQL,
  getUserGameSkillsGQL,
  getUserMostPlayedGamesGQL,
  listHotBattleGQL,
} from "../queries/games";
import { pick } from "lodash-es";
import { getGameSessionV2GQL } from "../queries/gameSessions";
import { getGlobalLeaderboardProfileGQL } from "../queries/globalLeaderboard";
import { listLiveStreams } from "../queries/liveStream";
import {
  getTournamentGQL,
  listMWCTournamentsGQL,
  listTournamentsGQL,
} from "../queries/tournament";
import {
  getUnseenRewardTransactionsGQL,
  getUserMetadataGQL,
  searchUserGQL,
} from "../queries/users";

export interface IParamsGetGame {
  id: string;
  momentStatus?: Status[];
  leaderboardLimit?: number;
  leaderboardOffset?: number;
}

export interface IParamsGetTournament {
  id: string;
  limit?: number;
  tournamentPlayerId?: string;
}

const apolloClient = apollo.getInstance();

export const getAchievements = async (
  userId?: string,
  options: Partial<QueryOptions> = {},
): Promise<Query["achievements"]> => {
  const res = await apolloClient.query({
    ...options,
    query: achievementsGQL,
    variables: {
      achievementsInput: {
        userId,
      },
    },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.achievements;
};

export const getMatch = async (
  matchId: string,
  options: Partial<QueryOptions> = {},
): Promise<Query["getMatch"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getMatchGQL,
    variables: { getMatchInput: { matchId } },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.getMatch;
};

export const getHouseMatch = async (
  matchId: string,
  options: Partial<QueryOptions> = {},
): Promise<Query["getHouseMatch"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getHouseMatchGQL,
    variables: { matchId },
    context: { api: ApiLegacyName.Legacy },
  });

  if (res?.errors) {
    throw res?.errors[0];
  }
  return res.data.getHouseMatch;
};

export const getUserHouseBattle = async (
  restrictToRecommended: boolean,
  options: Partial<QueryOptions> = {},
): Promise<Query["userHouseBattles"]> => {
  const res = await apolloClient.query({
    ...options,
    query: userHouseBattlesGQL,
    variables: { restrictToRecommended },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.userHouseBattles;
};

export const getChallenge = async (
  options: Partial<QueryOptions> = {},
): Promise<Query["challenges"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getChallengeGQL,
    variables: {},
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.challenges;
};

export const getContinuePlaying = async (
  options: Partial<QueryOptions> = {},
): Promise<Query["continuePlaying"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getContinuePlayingGQL,
    variables: {},
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.continuePlaying;
};

export const getContests = async (
  options: Partial<QueryOptions> = {},
): Promise<Query["contests"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getContestsGQL,
    variables: {},
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.contests;
};

export const getEventRules = async (
  options: Partial<QueryOptions> = {},
): Promise<Query["eventRules"]> => {
  const res = await apolloClient.query({
    ...options,
    query: eventRulesGQL,
    variables: {},
    context: { api: ApiLegacyName.Legacy },
  });

  return res?.data.eventRules;
};

export const getHouseBattleByGameId = async (
  id: string,
  options: Partial<QueryOptions> = {},
): Promise<HouseBattle[]> => {
  const res = await apolloClient.query({
    ...options,
    query: getHouseBattleByGameIdGQL,
    variables: {
      getGameInput: { id },
    },
    context: { api: ApiLegacyName.Legacy },
  });
  if (res?.errors) {
    throw res?.errors[0];
  }

  return res.data.getGame.houseBattles;
};

export const getGame = async (
  params: IParamsGetGame,
  options: Partial<QueryOptions> = {},
): Promise<Query["getGame"]> => {
  const gameInput = {
    id: params.id || "",
    momentStatus: params.momentStatus || [Status.Live],
    leaderboardLimit: params.momentStatus || 10,
    leaderboardOffset: params.momentStatus || 0,
  };

  const res = await apolloClient.query({
    ...options,
    query: getGameGQL,
    variables: {
      getGameInput: pick(gameInput, ["id", "momentStatus"]),
      getLeaderboardInput: {
        limit: gameInput.leaderboardLimit,
        offset: gameInput.leaderboardOffset,
      },
    },
    context: { api: ApiLegacyName.Legacy },
  });

  if (res?.errors) {
    throw res?.errors[0];
  }

  return res.data.getGame;
};

export const listHotBattle = async (
  options: Partial<QueryOptions> = {},
): Promise<Query["listHotBattles"]> => {
  const response = await apolloClient.query({
    ...options,
    query: listHotBattleGQL,
    variables: {},
    context: { api: ApiLegacyName.Legacy },
  });

  return response.data.listHotBattles;
};

export const fetchGameSkills = async (
  userId?: string,
  options = {},
): Promise<Query["listUserGameSkills"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getUserGameSkillsGQL,
    variables: { listUserGameSkillsInput: { userId } },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.listUserGameSkills;
};

export const listUserMostPlayedGames = async (
  options: Partial<QueryOptions> = {},
  userId?: string,
): Promise<Query["getUserMostPlayedGames"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getUserMostPlayedGamesGQL,
    variables: { getUserMostPlayedGamesInput: { userId } },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.getUserMostPlayedGames;
};

export const getGameSession = async (
  gameSessionId: string,
  options: Partial<QueryOptions> = {},
): Promise<Query["getGameSessionV2"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getGameSessionV2GQL,
    variables: { getGameSessionInput: { gameSessionId } },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.getGameSessionV2;
};

export const getGlobalLeaderboardProfile = async (
  endDate: string,
  leaderboardType: string,
  options: Partial<QueryOptions> = {},
): Promise<Query["globalLeaderboard"]> => {
  const response = await apolloClient.query({
    ...options,
    query: getGlobalLeaderboardProfileGQL,
    variables: {
      globalLeaderboardInput: {
        endDate: endDate,
        leaderboardType: leaderboardType,
      },
    },
    context: { api: ApiLegacyName.Legacy },
  });

  return response.data.globalLeaderboard;
};

export const listLivestreams = async (
  options: Partial<QueryOptions> = {},
  userId?: string,
): Promise<Query["livestreams"]> => {
  const res = await apolloClient.query({
    ...options,
    context: { api: ApiLegacyName.PrimaryLegacy },
    query: listLiveStreams,
    variables: { livestreamFilters: { userId, includeClosedStreams: true } },
  });

  return res?.data?.livestreams;
};

export const getTournament = async (
  params: IParamsGetTournament,
  options: Partial<QueryOptions> = {},
): Promise<Query["getTournament"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getTournamentGQL,
    variables: {
      getTournamentInput: { id: params.id },
      getLeaderboardInput: {
        limit: params?.limit ?? 10,
      },
    },
    context: { api: ApiLegacyName.Legacy },
  });

  if (res?.errors) {
    throw res?.errors[0];
  }
  return res.data.getTournament;
};

export const listMWCTournaments = async (
  params: {
    listTournamentInput?: Partial<ListTournamentInput>;
    getLeaderboardInput?: Partial<GetLeaderboardsInput>;
  },
  options: Partial<QueryOptions> = {},
): Promise<Query["listTournament"]> => {
  const res = await apolloClient.query({
    query: listMWCTournamentsGQL,
    variables: params,
    ...options,
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.listTournament;
};
export const listTournaments = async (
  params: {
    listTournamentInput?: Partial<ListTournamentInput>;
    getLeaderboardInput?: Partial<GetLeaderboardsInput>;
  },
  options: Partial<QueryOptions> = {},
): Promise<Query["listTournament"]> => {
  const res = await apolloClient.query({
    query: listTournamentsGQL,
    variables: params,
    ...options,
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.listTournament;
};

export const getUserMetadata = async (
  options: Partial<QueryOptions> = {},
): Promise<Query["getUserMetadata"]> => {
  const res = await apolloClient.query({
    ...options,
    query: getUserMetadataGQL,
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.getUserMetadata;
};

export const getUnseenRewardTransaction = async (
  options: Partial<QueryOptions> = {},
): Promise<FullTransaction[]> => {
  const res = await apolloClient.query({
    ...options,
    query: getUnseenRewardTransactionsGQL,
    variables: {},
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.getUserV2.unseenRewardTransactions;
};

export const searchUser = async (
  params: Partial<SearchUsersInput>,
  options: Partial<QueryOptions> = {},
): Promise<Query["users"]> => {
  const res = await apolloClient.query({
    ...options,
    query: searchUserGQL,
    variables: { searchUsersInput: params },
    context: { api: ApiLegacyName.Legacy },
  });

  return res.data.users;
};

export const getHouseBattles = async (
  gameId: string,
  options: Partial<QueryOptions> = {},
): Promise<Query["userHouseBattles"]> => {
  const response = await apolloClient.query({
    ...options,
    query: getHouseBattleByGameIdGQL,
    variables: {
      getGameInput: {
        id: gameId,
      },
    },
    context: { api: ApiLegacyName.Legacy },
  });

  return response.data.getGame.houseBattles;
};
